import React from 'react';
import { X } from 'lucide-react';
import useEscapeKey from '../hooks/useEscapeKey';

const getInvestmentCountLabel = (count) => {
  const labels = { 'less_than_10': 'Less than 10', '11_25': '11-25', 'more_than_25': 'More than 25' };
  return labels[count] || count;
};

const formatPhone = (r) =>
  r.phone_country_code && r.phone_number
    ? `${r.phone_country_code} ${r.phone_number}`
    : 'Not provided';

const formatLinkedIn = (url) =>
  url
    ? <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">View Profile</a>
    : 'Not provided';

const formatInvestmentTypes = (types) =>
  types?.length > 0 ? types.join(', ') : 'Not provided';

const DetailModalActions = ({ request, onApprove, onWaitlist, onClose }) => (
  <div className="flex gap-3 pt-4 border-t border-gray-700">
    <button
      onClick={() => { onApprove(request.request_id); onClose(); }}
      className="flex-1 px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition"
      data-testid="modal-approve-btn"
    >
      Approve Request
    </button>
    <button
      onClick={() => { onWaitlist(request.request_id); onClose(); }}
      className="flex-1 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition"
      data-testid="modal-waitlist-btn"
    >
      Add to Waitlist
    </button>
    <button onClick={onClose} className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition">
      Close
    </button>
  </div>
);

const getStatusBadge = (status) => {
  const styles = {
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
    approved: 'bg-green-500/20 text-green-400 border-green-500/50',
    rejected: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
  };
  const label = status === 'rejected' ? 'WAITLISTED' : status.toUpperCase();
  return <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[status] || ''}`}>{label}</span>;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const InfoSection = ({ title, children }) => (
  <div className="bg-gray-900 rounded-lg p-6">
    <h3 className="text-xl font-semibold text-white mb-4 border-b border-gray-700 pb-2">{title}</h3>
    <div className="grid grid-cols-2 gap-4">{children}</div>
  </div>
);

const InfoField = ({ label, children }) => (
  <div>
    <div className="text-sm text-gray-400">{label}</div>
    <div className="text-white font-medium">{children}</div>
  </div>
);

const getScoreColor = (score) => {
  if (score >= 100) return 'bg-green-900/20 border-green-500';
  if (score >= 50) return 'bg-yellow-900/20 border-yellow-500';
  return 'bg-red-900/20 border-red-500';
};

const getScoreLabel = (score) => {
  if (score >= 100) return 'Auto-Approve Recommended';
  if (score >= 50) return 'Manual Review Required';
  return 'Needs Follow-up';
};

export const AccessRequestDetailModal = ({ request, onClose, onApprove, onWaitlist }) => {
  useEscapeKey(onClose, !!request);
  if (!request) return null;

  const scoreColor = getScoreColor(request.approval_score);
  const scoreLabel = getScoreLabel(request.approval_score);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white">Access Request Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition p-1 rounded hover:bg-gray-700" data-testid="close-detail-modal" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Approval Score */}
          <div className={`p-6 rounded-lg border-2 ${scoreColor}`}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">Approval Score</h3>
                <p className="text-sm text-gray-400">{scoreLabel}</p>
              </div>
              <div className="text-5xl font-bold text-white">
                {request.approval_score || 0}<span className="text-2xl text-gray-400">/215</span>
              </div>
            </div>
          </div>

          <InfoSection title="Basic Information">
            <InfoField label="Name">{request.first_name} {request.last_name}</InfoField>
            <InfoField label="Email">{request.email}</InfoField>
            <InfoField label="Phone">{formatPhone(request)}</InfoField>
            <InfoField label="Status">{getStatusBadge(request.status)}</InfoField>
          </InfoSection>

          <InfoSection title="Professional Background">
            <InfoField label="Occupation">{request.occupation || 'Not provided'}</InfoField>
            <InfoField label="LinkedIn Profile">{formatLinkedIn(request.linkedin_profile)}</InfoField>
          </InfoSection>

          <InfoSection title="Investment Profile">
            <InfoField label="Investment Experience">{request.investment_experience || 'Not provided'}</InfoField>
            <InfoField label="Number of Investments">{getInvestmentCountLabel(request.investment_count)}</InfoField>
            <InfoField label="Portfolio Value">{request.portfolio_value || 'Not provided'}</InfoField>
            <InfoField label="Investment Types">{formatInvestmentTypes(request.investment_types)}</InfoField>
          </InfoSection>

          <InfoSection title="Financial Profile (Optional)">
            <InfoField label="Annual Income">{request.annual_income || 'Not provided'}</InfoField>
            <InfoField label="Net Worth">{request.net_worth || 'Not provided'}</InfoField>
          </InfoSection>

          {request.introduced_by && (
            <InfoSection title="Introduction Details">
              <InfoField label="Introduced By">{request.introduced_by}</InfoField>
              <InfoField label="Relationship">{request.relationship || 'Not specified'}</InfoField>
            </InfoSection>
          )}

          <InfoSection title="Request Metadata">
            <InfoField label="Request ID"><span className="font-mono text-xs">{request.request_id}</span></InfoField>
            <InfoField label="Created At">{formatDate(request.created_at)}</InfoField>
            <InfoField label="Email Verified">{request.email_verified ? 'Yes' : 'No'}</InfoField>
            {request.approved_at && <InfoField label="Approved At">{formatDate(request.approved_at)}</InfoField>}
          </InfoSection>

          {request.status === 'pending' && (
            <DetailModalActions request={request} onApprove={onApprove} onWaitlist={onWaitlist} onClose={onClose} />
          )}
        </div>
      </div>
    </div>
  );
};
